/**
 * Reviewable race-summary / race-setup insight thresholds and wording.
 */

import type { BetterDirection } from '../types';

export interface RaceMetricRule {
  id: string;
  metric: string;
  label: string;
  unit: string;
  threshold: number;
  better: BetterDirection;
  source: 'summary' | 'setup';
  /**
   * Conceptual family for dedupe across tables (e.g. race VMG % vs straight-line VMG %).
   * Summary rules are evaluated first — only one finding per concept is kept.
   */
  concept: string;
  headlineLoss: string;
  headlineGain: string;
  recommendation: string;
}

export const RACE_SUMMARY_RULES: RaceMetricRule[] = [
  {
    id: 'vmg_perc',
    metric: 'vmg_perc_avg',
    label: 'VMG %',
    unit: '%',
    threshold: 1.5,
    better: 'higher',
    source: 'summary',
    concept: 'vmg',
    headlineLoss: 'Behind the leaders on VMG %',
    headlineGain: 'Ahead of the leaders on VMG %',
    recommendation:
      'Close the VMG % gap — leaders averaged {value} ({leaders}). Check setup and mode against them.',
  },
  {
    id: 'start_speed',
    metric: 'start_speed',
    label: 'Start speed',
    unit: 'kph',
    threshold: 2,
    better: 'higher',
    source: 'summary',
    concept: 'start_speed',
    headlineLoss: 'Slower start speed than the leaders',
    headlineGain: 'Faster start speed than the leaders',
    recommendation:
      'Work the start acceleration package — leaders averaged {value} start speed ({leaders}).',
  },
  {
    id: 'tack_loss',
    metric: 'tack_loss_avg',
    label: 'Avg tack loss',
    unit: 'm',
    threshold: 5,
    better: 'lower',
    source: 'summary',
    concept: 'tack_loss',
    headlineLoss: 'Losing more in tacks than the leaders',
    headlineGain: 'Losing less in tacks than the leaders',
    recommendation:
      'Reduce tack loss toward the leaders\' {value} average ({leaders}).',
  },
  {
    id: 'gybe_loss',
    metric: 'gybe_loss_avg',
    label: 'Avg gybe loss',
    unit: 'm',
    threshold: 5,
    better: 'lower',
    source: 'summary',
    concept: 'gybe_loss',
    headlineLoss: 'Losing more in gybes than the leaders',
    headlineGain: 'Losing less in gybes than the leaders',
    recommendation:
      'Reduce gybe loss toward the leaders\' {value} average ({leaders}).',
  },
];

/**
 * Setup / straight-line table — trim & mode only.
 * Do not re-emit race-level outcomes already covered by RACE_SUMMARY_RULES
 * (VMG %, tack/gybe loss, start speed). Prefer the Results table card.
 */
export const RACE_SETUP_RULES: RaceMetricRule[] = [
  {
    id: 'setup_heel',
    metric: 'avg_heel',
    label: 'Avg heel',
    unit: 'deg',
    threshold: 1.0,
    better: 'higher',
    source: 'setup',
    concept: 'heel',
    headlineLoss: 'Heel differs from the leaders',
    headlineGain: 'Heel similar to the leaders',
    recommendation: 'Try leaders\' heel average of {value} ({leaders}).',
  },
  {
    id: 'setup_rh',
    metric: 'avg_rh',
    label: 'Avg ride height',
    unit: 'mm',
    threshold: 15,
    better: 'higher',
    source: 'setup',
    concept: 'ride_height',
    headlineLoss: 'Ride height differs from the leaders',
    headlineGain: 'Ride height similar to the leaders',
    recommendation: 'Try leaders\' ride height average of {value} ({leaders}).',
  },
  {
    id: 'setup_cant',
    metric: 'avg_cant',
    label: 'Avg cant',
    unit: 'deg',
    threshold: 1.0,
    better: 'higher',
    source: 'setup',
    concept: 'cant',
    headlineLoss: 'Cant differs from the leaders',
    headlineGain: 'Cant similar to the leaders',
    recommendation: 'Try leaders\' cant average of {value} ({leaders}).',
  },
  {
    id: 'setup_rud_rake',
    metric: 'avg_rud_rake',
    label: 'Avg rudder rake',
    unit: 'deg',
    threshold: 0.5,
    better: 'higher',
    source: 'setup',
    concept: 'rud_rake',
    headlineLoss: 'Rudder rake differs from the leaders',
    headlineGain: 'Rudder rake similar to the leaders',
    recommendation: 'Try leaders\' rudder rake average of {value} ({leaders}).',
  },
  {
    id: 'setup_wing_twist',
    metric: 'avg_wing_twist',
    label: 'Avg wing twist',
    unit: 'deg',
    threshold: 1.0,
    better: 'higher',
    source: 'setup',
    concept: 'wing_twist',
    headlineLoss: 'Wing twist differs from the leaders',
    headlineGain: 'Wing twist similar to the leaders',
    recommendation: 'Try leaders\' wing twist average of {value} ({leaders}).',
  },
  {
    id: 'setup_jib_lead',
    metric: 'avg_jib_lead',
    label: 'Avg jib lead',
    unit: 'deg',
    threshold: 1.0,
    better: 'higher',
    source: 'setup',
    concept: 'jib_lead',
    headlineLoss: 'Jib lead differs from the leaders',
    headlineGain: 'Jib lead similar to the leaders',
    recommendation: 'Try leaders\' jib lead average of {value} ({leaders}).',
  },
];
